import { intlModule } from '@chedri/base';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import withRouter from 'js/withRouter';
import { CartItem } from 'helpers/proptypes';
import { cartRemoveSimpleItem } from 'actions/CartActions';
import { getNameInCurrentLanguage } from 'helpers/intl';
import Validation from '../../validation/Validation';
import Headline from '../Headline';
import Properties from '../Properties';
import ValueAddedTax from '../ValueAddedTax';
import CartWizardStepActions from './CartWizardStepActions';
import CartWizardStepBody from './CartWizardStepBody';
import CartWizardStepContext from './CartWizardStepContext';
import CartWizardStepFrame from './CartWizardStepFrame';
import CartWizardStepHeader from './CartWizardStepHeader';

CartWizardSimpleItemStep.propTypes = {
  number: PropTypes.number.isRequired,
  cartItem: CartItem.isRequired,
  onConfirm: PropTypes.func,
};

CartWizardSimpleItemStep.defaultProps = {
  onConfirm: () => undefined,
};

function CartWizardSimpleItemStep({ number, cartItem, onConfirm }) {
  const dispatch = useDispatch();
  const messages = useSelector(intlModule.selectors.getMessages);
  const appIntl = useSelector(intlModule.selectors.getIntl);

  const [editableItem, setEditableItem] = useState(cartItem.item);
  const isCartPending = useSelector(state => !!state.cart.pending);

  const itemName = useMemo(() => {
    if (typeof cartItem?.name === 'string') {
      return cartItem.name;
    }

    if (Array.isArray(cartItem?.name)) {
      return getNameInCurrentLanguage(cartItem.name, appIntl) || cartItem.name.find(entry => entry?.name)?.name;
    }

    return cartItem?.label || 'Unknown';
  }, [cartItem, appIntl]);

  const details = useMemo(() => {
    return Array.isArray(cartItem?.details) ? cartItem.details : [];
  }, [cartItem]);

  const change = useCallback(
    data => {
      setEditableItem({ ...editableItem, ...data });
    },
    [editableItem, setEditableItem]
  );

  const domainAvailability = useCallback(() => undefined, []);

  const remove = useCallback(() => {
    console.log('Removing simple item from cart:', cartItem);
    dispatch(cartRemoveSimpleItem(cartItem.id));
  }, [dispatch, cartItem]);

  const configure = useCallback(() => undefined, []);

  return (
    <CartWizardStepContext.Provider
      value={{
        item: editableItem,
        origin: cartItem,
        change,
        confirm: onConfirm,
        remove,
        configure,
        domainAvailability,
      }}
    >
      <Validation rules={[]} validateOnValueChange={cartItem}>
        <div className="wizard-step">
          <CartWizardSimpleItemStep.Frame>
            <CartWizardSimpleItemStep.Header
              number={number}
              name={itemName}
              label={cartItem.label}
              campaign={cartItem?.campaign?.title || '-'}
              lastModified={new Date(cartItem.lastModified)}
              lastStored={new Date(cartItem.lastStored)}
              messages={messages}
            />
          </CartWizardSimpleItemStep.Frame>
          <CartWizardSimpleItemStep.Frame>
            <CartWizardSimpleItemStep.Body pending={isCartPending}>
              <Row>
                <Col xs={8}>
                  <Headline description={messages.product_details}>{messages.product_details}</Headline>
                  <Properties data={details} />
                </Col>
                <Col xs={4}>
                  <ValueAddedTax
                    net={cartItem.price}
                    gross={cartItem.gross}
                    vat={cartItem.vat}
                    currency={cartItem.currency}
                    webPdfPrice={0}
                    manualDataCheckPrice={0}
                    isItemAllowsReducedVatRate={false}
                  />
                </Col>
              </Row>
            </CartWizardSimpleItemStep.Body>
            <CartWizardSimpleItemStep.Actions cartItem={cartItem} />
          </CartWizardSimpleItemStep.Frame>
        </div>
      </Validation>
    </CartWizardStepContext.Provider>
  );
}

CartWizardSimpleItemStep.Frame = CartWizardStepFrame;
CartWizardSimpleItemStep.Header = CartWizardStepHeader;
CartWizardSimpleItemStep.Body = CartWizardStepBody;
CartWizardSimpleItemStep.Actions = CartWizardStepActions;

export default withRouter(injectIntl(CartWizardSimpleItemStep));
